import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/roles";
import { toast } from "sonner";
import { Plus, Trash2, HardHat, CheckCircle2 } from "lucide-react";

export const PCW_ITEMS = [
  ["application_onboarding", "Application & onboarding"],
  ["i9_audit", "I-9 compliance audit"],
  ["orientation", "PCW orientation"],
  ["background_check", "Background check cleared"],
  ["forwardhealth_evv", "ForwardHealth & EVV entry"],
  ["sample_timesheet", "Sample timesheet prepared"],
];

function PcwCard({ pcw, canEdit, onSaved, onDeleted }) {
  const [p, setP] = useState(pcw);
  useEffect(() => setP(pcw), [pcw]);
  const done = PCW_ITEMS.filter(([k]) => (p.onboarding || {})[k]).length;
  const total = PCW_ITEMS.length;
  const pct = Math.round((done / total) * 100);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/pcws/${pcw.id}`, body);
      setP(data);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleItem = (key, val) => patch({ onboarding: { [key]: val } });
  const remove = async () => {
    if (!window.confirm(`Delete PCW "${p.name}"? Any client linked to them will be unlinked.`)) return;
    try { await api.delete(`/pcws/${pcw.id}`); onDeleted(pcw.id); toast.success("PCW deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`pcw-card-${pcw.id}`} className="bg-white border border-[#15233B]/10 rounded-md p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {canEdit ? (
            <input
              value={p.name}
              data-testid={`pcw-name-${pcw.id}`}
              onChange={(e) => setP({ ...p, name: e.target.value })}
              onBlur={() => p.name !== pcw.name && patch({ name: p.name })}
              className="text-base font-extrabold tracking-tight text-[#15233B] border border-transparent hover:border-[#15233B]/15 focus:border-[#15233B]/30 rounded px-1 -ml-1 w-full bg-white"
            />
          ) : (
            <div className="text-base font-extrabold tracking-tight text-[#15233B]">{p.name}</div>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {canEdit ? (
              <input
                value={p.phone || ""}
                placeholder="phone"
                onChange={(e) => setP({ ...p, phone: e.target.value })}
                onBlur={() => (p.phone || "") !== (pcw.phone || "") && patch({ phone: p.phone })}
                className="text-xs text-[#15233B]/70 border border-transparent hover:border-[#15233B]/15 rounded px-1 -ml-1 bg-white"
              />
            ) : (
              <span className="text-xs text-[#15233B]/70">{p.phone || "—"}</span>
            )}
            {p.active === false && <span className="text-[9px] uppercase tracking-wide text-[#15233B]/40">(inactive)</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {done === total ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-[#166534] bg-[#166534]/10 px-2 py-1 rounded">
              <CheckCircle2 className="h-3 w-3" /> Cleared
            </span>
          ) : (
            <span className="text-[10px] font-bold text-[#E65100] bg-[#E65100]/10 px-2 py-1 rounded">{done}/{total}</span>
          )}
          {canEdit && (
            <button onClick={remove} data-testid={`pcw-delete-${pcw.id}`} className="p-1 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5"><Trash2 className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      <div className="mt-2 h-1.5 w-full bg-[#F1F2F4] rounded-full overflow-hidden">
        <div className="h-full bg-[#166534] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {PCW_ITEMS.map(([key, label]) => {
          const checked = !!(p.onboarding || {})[key];
          return (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid={`pcw-${pcw.id}-${key}`}
                checked={checked}
                disabled={!canEdit}
                onChange={(e) => toggleItem(key, e.target.checked)}
                className="h-4 w-4 rounded border-[#15233B]/30 text-[#166534] focus:ring-[#166534]"
              />
              <span className={checked ? "line-through text-[#15233B]/45" : "text-[#15233B]"}>{label}</span>
            </label>
          );
        })}
      </div>

      {canEdit && (
        <label className="mt-3 flex items-center gap-2 text-[11px] text-[#15233B]/60">
          <input type="checkbox" checked={!!p.active} onChange={(e) => patch({ active: e.target.checked })} className="h-3.5 w-3.5 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]" />
          Active
        </label>
      )}
    </div>
  );
}

export default function PcwsPage() {
  const { user } = useAuth();
  const canEdit = isManager(user);
  const [pcws, setPcws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/pcws").then(({ data }) => { setPcws(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data } = await api.post("/pcws", { name: name.trim(), phone: phone.trim() });
      setPcws((p) => [...p, data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
      setName(""); setPhone("");
      toast.success("PCW added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cleared = pcws.filter((p) => p.onboarding_complete).length;

  return (
    <div className="min-h-screen flex flex-col onboarding-bg">
      <Header />
      <div aria-hidden className="fixed inset-0 z-0 pointer-events-none bg-no-repeat" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/brand/avida-logo.png)`, backgroundPosition: "center 52%", backgroundSize: "min(640px, 72vw)", opacity: 0.05 }} />
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <HardHat className="h-5 w-5 text-[#FBBF24]" />
          <h1 className="text-2xl font-extrabold tracking-tight text-[#15233B]">Personal Care Workers</h1>
        </div>
        <p className="text-sm text-[#15233B]/60 mb-6">
          Track each caregiver's onboarding &amp; compliance once. {cleared}/{pcws.length} fully cleared.
          {!canEdit && " (View only — managers and owners can edit.)"}
        </p>

        {canEdit && (
          <div data-testid="pcw-add" className="flex flex-col sm:flex-row gap-2 items-end mb-6 bg-white border border-[#15233B]/10 rounded-md p-3">
            <div className="flex-1 w-full">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">PCW name</label>
              <input data-testid="pcw-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Maria Lopez" className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
            </div>
            <div className="w-full sm:w-48">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Phone (optional)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
            </div>
            <button onClick={create} data-testid="pcw-create" className="px-4 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] inline-flex items-center justify-center gap-1 shrink-0"><Plus className="h-4 w-4" /> Add PCW</button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-[#15233B]/50">Loading…</div>
        ) : pcws.length === 0 ? (
          <div className="text-sm text-[#15233B]/50 italic bg-white border border-[#15233B]/10 rounded-md p-6 text-center">No PCWs yet.{canEdit ? " Add one above." : ""}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pcws.map((p) => (
              <PcwCard key={p.id} pcw={p} canEdit={canEdit}
                onSaved={(d) => setPcws((prev) => prev.map((x) => (x.id === d.id ? d : x)))}
                onDeleted={(id) => setPcws((prev) => prev.filter((x) => x.id !== id))} />
            ))}
          </div>
        )}
      </div>
      <BrandFooter />
    </div>
  );
}
